import { useFormatter, useLocale, useTranslations } from 'next-intl'
import { useCallback } from 'react'

import { DAYS_OF_WEEK, type DayOfWeek } from '~/components/forms/DaysOfWeekInput'

/** RRULE/ISO-8601 weekday tokens, Monday-first (the schedule vocabulary). */
export const WEEKDAY_TOKENS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const
export type WeekdayToken = (typeof WEEKDAY_TOKENS)[number]

const TOKEN_TO_DAY: Record<WeekdayToken, DayOfWeek> = {
  MO: 'MONDAY',
  TU: 'TUESDAY',
  WE: 'WEDNESDAY',
  TH: 'THURSDAY',
  FR: 'FRIDAY',
  SA: 'SATURDAY',
  SU: 'SUNDAY'
}

type DayOfWeekInput = DayOfWeek | WeekdayToken

function toDayOfWeek(day: DayOfWeekInput): DayOfWeek {
  return day in TOKEN_TO_DAY ? TOKEN_TO_DAY[day as WeekdayToken] : (day as DayOfWeek)
}

type DaysOfWeekOptions = {
  /**
   * 'narrow' (default) uses the app's one/two-letter abbreviations from the
   * locale files; 'short'/'long' use the locale's standard Intl weekday names
   * ("Mon" / "Monday"), which need no translation keys.
   */
  style?: 'narrow' | 'short' | 'long'
}

const weekdays: DayOfWeek[] = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY']
const weekends: DayOfWeek[] = ['SATURDAY', 'SUNDAY']

/**
 * Formats one or more days of the week. Accepts full day names and/or RRULE
 * weekday tokens; full sets collapse to "everyday" / "weekdays" / "weekends".
 * Everything else renders in week order per `options.style` — the narrow
 * abbreviations as a comma list, the Intl short/long names as a conjunction
 * list ("Monday, Wednesday, and Friday").
 */
export default function useDaysOfWeekFormatter() {
  const t = useTranslations('data.days')
  const locale = useLocale()
  const intl = useFormatter()

  return useCallback(
    (days?: DayOfWeekInput | DayOfWeekInput[], options?: DaysOfWeekOptions) => {
      if (!days) return ''

      const style = options?.style ?? 'narrow'
      const normalized = (Array.isArray(days) ? days : [days]).map(toDayOfWeek)

      if (normalized.length === 7) return t('everyday')

      // Check if the selected days exactly match weekdays or weekends
      const isWeekdays = weekdays.length === normalized.length && weekdays.every((day) => normalized.includes(day))
      const isWeekends = weekends.length === normalized.length && weekends.every((day) => normalized.includes(day))

      if (isWeekdays) return t('weekdays')
      if (isWeekends) return t('weekends')

      // Sort days according to Monday->Sunday order and format per style
      const sortedDays = DAYS_OF_WEEK.filter((day) => normalized.includes(day))
      if (style === 'narrow') return sortedDays.map((day) => t(`abbreviations_${day}`)).join(', ')
      // 2024-01-01 is a Monday; offset it by the day's week position so only the weekday matters.
      const dtf = new Intl.DateTimeFormat(locale, { weekday: style, timeZone: 'UTC' })
      const names = sortedDays.map((day) => dtf.format(new Date(Date.UTC(2024, 0, 1 + DAYS_OF_WEEK.indexOf(day)))))
      return intl.list(names, { type: 'conjunction' })
    },
    [t, locale, intl]
  )
}
